<script lang="ts">
	//UI
	import { Card, Pagenation } from "@hitit/new_ui/src";
	import DefaultLoading from "@hitit/new_ui/src/SVG/DefaultLoading.svelte";
	//TYPE
	import type { UserEventLogDTO } from "@hitit/api/src/types";
	type LogEventShowStatus = {
		show: boolean;
	};
	//UTILS
	import { format } from "date-fns";
	import { api, t } from "@hitit/hub/src/stores";
	import { onDestroy } from "svelte";
	import { popup } from "@hitit/hub/src/v7/store";

	//Props
	export let userId = "";

	//Constants
	const colHeaderType = ["time", "deviceType", "loglevel", "module", "message"];
	const logDetailShowStatus: { [key: string]: LogEventShowStatus } = {};

	//Variables
	let presentPage: number = 0;
	let totalPage: number = presentPage;
	let loading = false;
	let showEventLogDTO: UserEventLogDTO[] = [];

	//Functions
	async function loadLogs(presentPage: number, userId: string) {
		try {
			loading = true;
			const { data } = await $api.getUserEventLog(presentPage, userId);

			showEventLogDTO = data.userEventLogDTOs;
			totalPage = data.totalPages;
		} catch {
			console.log("error");
		} finally {
			loading = false;
		}
	}
	function processingDate(date: string) {
		const formatDate = new Date(date + "Z");
		return format(formatDate, "yyyy.MM.dd HH:mm");
	}

	function handleRow(dto: UserEventLogDTO) {
		if (logDetailShowStatus[dto.id] === undefined) {
			logDetailShowStatus[dto.id] = {
				show: true,
			};
		} else {
			const show = logDetailShowStatus[dto.id].show;
			logDetailShowStatus[dto.id].show = !show;
		}
	}

	//Do
	$: {
		loadLogs(presentPage, userId);
	}

	onDestroy(() => {
		$popup = undefined;
	});

</script>

<div class="container">
	<Card noPadding>
		<div class="card-content">
			<h1 class="title">{$t('mbr.debug.eventLogging')}</h1>
			{#if loading}
				<div class="loading">
					<DefaultLoading />
				</div>
			{:else}
				<div class="head">
					{#each colHeaderType as type}
						<div class="col-header {type}">
							{$t(`mbr.debug.eventLogColumn.${type}`)}
						</div>
					{/each}
				</div>

				<div class="body">
					{#each showEventLogDTO as log}
						<div class="row" on:click={() => handleRow(log)}>
							<div class="item time">{processingDate(log.createdDate)}</div>
							<div class="item deviceType">{log.mdevicetype}</div>
							<div class="item loglevel">{log.logLevel}</div>
							<div class="item module">{log.module}</div>
							<div class="item message">{log.message}</div>
						</div>
						{#if logDetailShowStatus[log.id || '']?.show}
							<div class="detail-box">
								<div class="detail-contents">
									{#each Object.entries(log) as [key, value]}
										<div>
											{@html `"${key}" : "${value ? value.replaceAll('\n', '</br>') : ''}"`}
										</div>
									{/each}
								</div>
							</div>
						{/if}
					{/each}
				</div>
				<div class="pagenation">
					<Pagenation
						totalPage={totalPage}
						presentPage={presentPage}
						buttonCallback={(page) => (presentPage = page)} />
				</div>
			{/if}
		</div>
	</Card>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	.container {
		.card-content {
			width: 100%;

			.loading {
				width: 30px;
				height: 30px;
				display: flex;
				justify-content: center;
				align-items: center;
				margin: 0 auto;
			}
		}
		.title {
			color: #46474b;
			margin: 0;
			font-size: 1.25rem;
			font-weight: bold;
			padding: 20px 10px;
		}
		.head {
			display: none;
		}

		.row {
			position: relative;
			display: flex;
			flex-direction: column;
			gap: 10px;
			padding: 10px 10px;
			cursor: pointer;
			&:not(:last-child) {
				border-bottom: 1px solid #e6e7ec;
			}
		}
		.detail-box {
			padding: 20px 10px;
			background-color: #e6e7ed;
			.detail-contents {
				border-radius: 5px;
				line-height: 22px;
				padding: 10px 10px;
				background-color: white;
				word-break: break-all;
				display: flex;
				flex-direction: column;
				gap: 5px;
				@include appleAdjustTextForSize(16);
			}
		}

		.item {
			color: #46474b;
		}

		.time {
			position: absolute;
			bottom: 20px;
			right: 10px;
			font-size: 0.875rem;
			color: #6d6f76;
		}
		.deviceType {
			order: 2;
		}
		.loglevel {
			order: 1;
			font-size: 1rem;
			font-weight: 500;
		}
		.module {
			order: 3;
		}
		.message {
			display: none;
		}

		.pagenation {
			padding: 20px 0px;
		}
	}

	@include DesktopSize {
		.container {
			.title {
				font-size: 2rem;
				padding-top: 30px;
				padding-left: 30px;
			}
			.head {
				display: flex;
				justify-content: space-between;
				padding: 10px 30px 0px 30px;
			}
			.row {
				display: flex;
				justify-content: space-between;
				flex-direction: row;
				flex-wrap: wrap;
				padding: 20px 20px;
			}
			.detail-box {
				padding: 20px;
				.detail-contents {
					@include appleAdjustTextForSize(16, false);
				}
			}
			.col-header {
				display: flex;
				justify-content: center;
				letter-spacing: -0.8px;
				width: calc((100% - 145px - 350px) * 0.65);
				font-size: 1.25rem;
				color: #6d6f76;
			}

			.item {
				display: flex;
				justify-content: center;
				letter-spacing: -0.8px;
				width: calc((100% - 150px - 350px) * 0.65);
				font-size: 1.25rem;
				color: #46474b;
			}
			.time {
				width: 150px;
				max-width: 150px;
				position: static;
				color: #46474b;
			}
			.deviceType {
				min-width: 120px;
				max-width: calc((100% - 150px - 350px) * (260 / 1530));
				order: unset;
			}
			.loglevel {
				min-width: 60px;
				max-width: calc((100% - 150px - 350px) * (260 / 1530));
				order: unset;
				font-weight: 200;
			}
			.module {
				min-width: 60px;
				max-width: calc((100% - 150px - 350px) * (260 / 1530));
				order: unset;
			}
			.message {
				display: block;
				min-width: 180px;
				max-width: calc((100% - 150px - 350px) * (700 / 1530));
				text-align: center;
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;
			}

			.pagenation {
				padding: 30px 0px;
			}
		}
	}

</style>
